import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type {
  SaveStudyPlanCheckinPayload,
  SaveStudyPlanCheckinResponse,
  SaveStudyPlanPayload,
  SaveStudyPlanResponse,
  StudyPlan,
  StudyPlanCheckin,
  StudyPlanDraftSummary,
  StudyPlanMilestone,
  StudyPlanTask,
  TodayStudyPlan,
} from "@/types/study-plan";
import { requestSaveStudyPlan, requestSaveStudyPlanCheckin } from "@/lib/study-plan-client";
import type { ReaderAiBlock, ReaderAiMessage } from "@/types/agentic-chat";

type StudyPlanRow = Tables<"study_plans">;
type StudyPlanConstraintRow = Tables<"study_plan_constraints">;
type StudyPlanMilestoneRow = Tables<"study_plan_milestones">;
type StudyPlanTaskRow = Tables<"study_plan_tasks">;
type StudyPlanCheckinRow = Tables<"study_plan_checkins">;

type StudyPlanTaskWithResource = StudyPlanTaskRow & {
  resource?: {
    id: string;
    title: string;
  } | null;
};

const todayKey = () => new Date().toISOString().slice(0, 10);

export const deriveStudyPlanGoalType = (text: string): StudyPlan["goalType"] => {
  const normalized = text.toLowerCase();
  if (/(exam|midterm|final|امتحان|فاينل|ميد)/i.test(normalized)) return "exam";
  if (/(catch up|catch-up|لحاق|تراكمي)/i.test(normalized)) return "course_catchup";
  if (/(routine|weekly|week|أسبوع|روتين)/i.test(normalized)) return "weekly_routine";
  if (/(master|mastery|أتقن|إتقان)/i.test(normalized)) return "topic_mastery";
  return "custom";
};

export const buildSaveStudyPlanPayloadFromMessage = ({
  threadId,
  message,
  linkedResourceId,
  weakTopics = [],
  savedFrom = "toofi_workspace",
}: {
  threadId: string;
  message: ReaderAiMessage;
  linkedResourceId?: string | null;
  weakTopics?: string[];
  savedFrom?: "reader_ai" | "toofi_workspace";
}): SaveStudyPlanPayload | null => {
  const planOverview = message.blocks.find((block): block is Extract<ReaderAiBlock, { type: "plan_overview" }> => block.type === "plan_overview");
  if (!planOverview) return null;

  const milestoneBlock = message.blocks.find((block): block is Extract<ReaderAiBlock, { type: "plan_milestones" }> => block.type === "plan_milestones");
  const dailyPlanBlock = message.blocks.find((block): block is Extract<ReaderAiBlock, { type: "daily_plan" }> => block.type === "daily_plan");

  const fallbackTasks =
    milestoneBlock?.items.map((item, index) => ({
      id: `milestone-task-${index}`,
      title: item.title,
      scheduledDate: dailyPlanBlock?.date ?? new Date().toISOString().slice(0, 10),
      sortOrder: index,
      milestoneId: item.id,
      status: "pending" as const,
      createdBy: "ai" as const,
    })) ?? [];

  return {
    plan: {
      title: planOverview.title,
      goalType: deriveStudyPlanGoalType(`${planOverview.title} ${planOverview.goalSummary}`),
      goalSummary: planOverview.goalSummary,
      startDate: new Date().toISOString().slice(0, 10),
      targetDate: planOverview.targetDate ?? null,
      estimatedTotalMinutes: planOverview.estimatedTotalMinutes ?? null,
      createdFromThreadId: threadId,
      source: "ai",
      planningContext: {
        horizonLabel: planOverview.horizonLabel,
        resourceId: linkedResourceId ?? null,
        savedFrom,
      },
      planSummary: planOverview.goalSummary,
    },
    constraints: {
      resourceIds: linkedResourceId ? [linkedResourceId] : [],
      weakTopics: weakTopics.slice(0, 6),
    },
    milestones:
      milestoneBlock?.items.map((item, index) => ({
        id: item.id,
        title: item.title,
        sortOrder: index,
        targetDate: item.targetDate ?? null,
        status: item.status ?? "pending",
        description: item.summary ?? null,
      })) ?? [],
    tasks:
      dailyPlanBlock?.items.map((item, index) => ({
        id: item.id,
        milestoneId: milestoneBlock?.items[index]?.id ?? null,
        title: item.text,
        scheduledDate: dailyPlanBlock.date,
        estimatedMinutes: item.estimatedMinutes ?? null,
        sortOrder: index,
        status: item.checked ? "completed" : "pending",
        resourceId: item.resourceId ?? null,
        resourceTitle: item.resourceTitle ?? null,
        createdBy: "ai" as const,
      })) ?? fallbackTasks,
    activate: true,
    changeReason: "Confirmed from Toofi planning conversation",
  };
};

export const studyPlanQueryKeys = {
  active: (userId?: string | null) => ["study-plan", "active", userId] as const,
  today: (userId?: string | null) => ["study-plan", "today", userId] as const,
  drafts: (userId?: string | null) => ["study-plan", "drafts", userId] as const,
  checkins: (userId?: string | null, planId?: string | null) => ["study-plan", "checkins", userId, planId] as const,
};

const normalizeMilestone = (row: StudyPlanMilestoneRow): StudyPlanMilestone => ({
  id: row.id,
  title: row.title,
  description: row.description,
  sortOrder: row.sort_order,
  targetDate: row.target_date,
  status: row.status as StudyPlanMilestone["status"],
  progressPercent: row.progress_percent,
});

const normalizeTask = (row: StudyPlanTaskWithResource): StudyPlanTask => ({
  id: row.id,
  planId: row.plan_id,
  milestoneId: row.milestone_id,
  title: row.title,
  description: row.description,
  scheduledDate: row.scheduled_date,
  estimatedMinutes: row.estimated_minutes,
  sortOrder: row.sort_order,
  status: row.status as StudyPlanTask["status"],
  completionOrigin: row.completion_origin as StudyPlanTask["completionOrigin"],
  resourceId: row.resource_id,
  resourceTitle: row.resource?.title ?? null,
  sourceTopic: row.source_topic,
  createdBy: row.created_by as StudyPlanTask["createdBy"],
  completedAt: row.completed_at,
});

const normalizeCheckin = (row: StudyPlanCheckinRow): StudyPlanCheckin => ({
  id: row.id,
  planId: row.plan_id,
  checkinType: row.checkin_type as StudyPlanCheckin["checkinType"],
  status: (row.status ?? "on_track") as StudyPlanCheckin["status"],
  summary: row.summary,
  mood: row.mood,
  loadRating: row.load_rating,
  confidenceRating: row.confidence_rating,
  completionRating: row.completion_rating,
  recommendedAction: row.recommended_action,
  createdBy: row.created_by as StudyPlanCheckin["createdBy"],
  createdAt: row.created_at,
});

const sortMilestones = (items: StudyPlanMilestone[]) =>
  [...items].sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));

const sortTasks = (items: StudyPlanTask[]) =>
  [...items].sort((a, b) => {
    const dateA = a.scheduledDate ?? "9999-12-31";
    const dateB = b.scheduledDate ?? "9999-12-31";
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return a.sortOrder - b.sortOrder || a.title.localeCompare(b.title);
  });

const buildPlanGraph = ({
  plan,
  constraints,
  milestones,
  tasks,
}: {
  plan: StudyPlanRow;
  constraints: StudyPlanConstraintRow | null;
  milestones: StudyPlanMilestoneRow[];
  tasks: StudyPlanTaskWithResource[];
}): StudyPlan => ({
  id: plan.id,
  userId: plan.user_id,
  title: plan.title,
  status: plan.status as StudyPlan["status"],
  goalType: plan.goal_type as StudyPlan["goalType"],
  goalSummary: plan.goal_summary,
  startDate: plan.start_date,
  targetDate: plan.target_date,
  estimatedTotalMinutes: plan.estimated_total_minutes,
  createdFromThreadId: plan.created_from_thread_id,
  source: plan.source as StudyPlan["source"],
  planningContext:
    typeof plan.planning_context === "object" && plan.planning_context !== null
      ? (plan.planning_context as Record<string, unknown>)
      : {},
  planSummary: plan.plan_summary,
  lastReplannedAt: plan.last_replanned_at,
  createdAt: plan.created_at,
  updatedAt: plan.updated_at,
  constraints: constraints
    ? {
        availableDays: constraints.available_days,
        availableTimeWindows: constraints.available_time_windows,
        preferredSessionMinutes: constraints.preferred_session_minutes,
        sessionsPerDay: constraints.sessions_per_day,
        intensity: constraints.intensity as StudyPlan["constraints"]["intensity"],
        planningConfidence: constraints.planning_confidence as StudyPlan["constraints"]["planningConfidence"],
        difficultyNotes: constraints.difficulty_notes,
        resourceIds: constraints.resource_ids,
        weakTopics: constraints.weak_topics,
        mustCoverTopics: constraints.must_cover_topics,
        avoidTopics: constraints.avoid_topics,
      }
    : null,
  milestones: sortMilestones(milestones.map(normalizeMilestone)),
  tasks: sortTasks(tasks.map(normalizeTask)),
});

const fetchPlanGraph = async (planRow: StudyPlanRow): Promise<StudyPlan> => {
  const [{ data: constraintRow, error: constraintError }, { data: milestoneRows, error: milestoneError }, { data: taskRows, error: taskError }] =
    await Promise.all([
      supabase.from("study_plan_constraints").select("*").eq("plan_id", planRow.id).maybeSingle(),
      supabase.from("study_plan_milestones").select("*").eq("plan_id", planRow.id).order("sort_order", { ascending: true }),
      supabase
        .from("study_plan_tasks")
        .select("*, resource:resources(id,title)")
        .eq("plan_id", planRow.id)
        .order("sort_order", { ascending: true })
        .order("scheduled_date", { ascending: true }),
    ]);

  if (constraintError) throw constraintError;
  if (milestoneError) throw milestoneError;
  if (taskError) throw taskError;

  return buildPlanGraph({
    plan: planRow,
    constraints: (constraintRow as StudyPlanConstraintRow | null) ?? null,
    milestones: (milestoneRows ?? []) as StudyPlanMilestoneRow[],
    tasks: (taskRows ?? []) as StudyPlanTaskWithResource[],
  });
};

export async function fetchActiveStudyPlan(userId: string): Promise<StudyPlan | null> {
  const { data, error } = await supabase
    .from("study_plans")
    .select("*")
    .eq("user_id", userId)
    .eq("status", "active")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  return fetchPlanGraph(data as StudyPlanRow);
}

export async function fetchStudyPlanDrafts(userId: string): Promise<StudyPlanDraftSummary[]> {
  const { data, error } = await supabase
    .from("study_plans")
    .select("id,title,status,goal_type,goal_summary,target_date,updated_at")
    .eq("user_id", userId)
    .in("status", ["draft", "archived"])
    .order("updated_at", { ascending: false })
    .limit(6);

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    title: row.title,
    status: row.status as StudyPlanDraftSummary["status"],
    goalType: row.goal_type as StudyPlanDraftSummary["goalType"],
    goalSummary: row.goal_summary,
    targetDate: row.target_date,
    updatedAt: row.updated_at,
  }));
}

export async function fetchStudyPlanCheckins(userId: string, planId: string): Promise<StudyPlanCheckin[]> {
  const { data: planRow, error: planError } = await supabase
    .from("study_plans")
    .select("id")
    .eq("id", planId)
    .eq("user_id", userId)
    .maybeSingle();

  if (planError) throw planError;
  if (!planRow) return [];

  const { data, error } = await supabase
    .from("study_plan_checkins")
    .select("*")
    .eq("plan_id", planId)
    .order("created_at", { ascending: false })
    .limit(12);

  if (error) throw error;
  return (data ?? []).map(normalizeCheckin);
}

export async function fetchTodayStudyPlan(userId: string): Promise<TodayStudyPlan | null> {
  const plan = await fetchActiveStudyPlan(userId);
  if (!plan) return null;

  const today = todayKey();
  const carryOverItems = plan.tasks.filter(
    (task) => task.status !== "completed" && Boolean(task.scheduledDate) && task.scheduledDate! < today,
  );
  const todayItems = plan.tasks.filter((task) => task.scheduledDate === today);
  const tasks = sortTasks([
    ...carryOverItems.map((task) => ({ ...task, status: task.status === "pending" ? "carried_forward" : task.status })),
    ...todayItems,
  ]);

  const completedCount = tasks.filter((task) => task.status === "completed").length;
  const totalMinutes = tasks.reduce((sum, task) => sum + (task.estimatedMinutes ?? 0), 0);

  return {
    plan,
    dateKey: today,
    tasks,
    totalMinutes,
    completedCount,
    isEmpty: tasks.length === 0,
    allCompleted: tasks.length > 0 && completedCount === tasks.length,
    behindSchedule: carryOverItems.length > 0,
  };
}

export async function saveStudyPlan(payload: SaveStudyPlanPayload): Promise<SaveStudyPlanResponse> {
  return requestSaveStudyPlan(payload);
}

export async function saveStudyPlanCheckin(
  payload: SaveStudyPlanCheckinPayload,
): Promise<SaveStudyPlanCheckinResponse> {
  return requestSaveStudyPlanCheckin(payload);
}

const syncMilestoneProgress = async (planId: string) => {
  const [{ data: milestones, error: milestoneError }, { data: tasks, error: taskError }] = await Promise.all([
    supabase.from("study_plan_milestones").select("*").eq("plan_id", planId),
    supabase.from("study_plan_tasks").select("id,milestone_id,status").eq("plan_id", planId),
  ]);

  if (milestoneError) throw milestoneError;
  if (taskError) throw taskError;

  const milestoneRows = (milestones ?? []) as StudyPlanMilestoneRow[];
  const taskRows = (tasks ?? []) as Pick<StudyPlanTaskRow, "id" | "milestone_id" | "status">[];

  await Promise.all(
    milestoneRows.map(async (milestone) => {
      const related = taskRows.filter((task) => task.milestone_id === milestone.id);
      const completed = related.filter((task) => task.status === "completed").length;
      const progressPercent = related.length ? Math.round((completed / related.length) * 100) : 0;
      const status =
        related.length > 0 && completed === related.length
          ? "completed"
          : completed > 0
            ? "active"
            : milestone.status === "skipped"
              ? "skipped"
              : "pending";

      await supabase
        .from("study_plan_milestones")
        .update({
          progress_percent: progressPercent,
          status,
          updated_at: new Date().toISOString(),
        })
        .eq("id", milestone.id)
        .eq("plan_id", planId);
    }),
  );
};

export async function updateStudyPlanTaskStatus({
  userId,
  taskId,
  status,
}: {
  userId: string;
  taskId: string;
  status: StudyPlanTask["status"];
}) {
  const { data: taskRow, error: taskLookupError } = await supabase
    .from("study_plan_tasks")
    .select("id,plan_id,status")
    .eq("id", taskId)
    .maybeSingle();

  if (taskLookupError) throw taskLookupError;
  if (!taskRow) throw new Error("Task not found");

  const { data: planRow, error: planError } = await supabase
    .from("study_plans")
    .select("id")
    .eq("id", taskRow.plan_id)
    .eq("user_id", userId)
    .maybeSingle();

  if (planError) throw planError;
  if (!planRow) throw new Error("Plan not found");

  const nextStatus = status;
  const { error: updateError } = await supabase
    .from("study_plan_tasks")
    .update({
      status: nextStatus,
      completion_origin: nextStatus === "completed" ? "student" : null,
      completed_at: nextStatus === "completed" ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", taskId)
    .eq("plan_id", taskRow.plan_id);

  if (updateError) throw updateError;

  await syncMilestoneProgress(taskRow.plan_id);

  await supabase
    .from("study_plans")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", taskRow.plan_id)
    .eq("user_id", userId);
}

export async function updateStudyPlan({
  userId,
  planId,
  title,
  goalSummary,
  targetDate,
}: {
  userId: string;
  planId: string;
  title: string;
  goalSummary: string;
  targetDate?: string | null;
}) {
  const nextTitle = title.trim();
  const nextGoalSummary = goalSummary.trim();
  if (!nextTitle || !nextGoalSummary) {
    throw new Error("Plan title and summary are required");
  }

  const { data: planRow, error: planError } = await supabase
    .from("study_plans")
    .select("id")
    .eq("id", planId)
    .eq("user_id", userId)
    .maybeSingle();

  if (planError) throw planError;
  if (!planRow) throw new Error("Plan not found");

  const { error: updateError } = await supabase
    .from("study_plans")
    .update({
      title: nextTitle,
      goal_summary: nextGoalSummary,
      plan_summary: nextGoalSummary,
      target_date: targetDate?.trim() ? targetDate : null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", planId)
    .eq("user_id", userId);

  if (updateError) throw updateError;
}

export async function deleteStudyPlan({
  userId,
  planId,
}: {
  userId: string;
  planId: string;
}) {
  const { data: planRow, error: planError } = await supabase
    .from("study_plans")
    .select("id")
    .eq("id", planId)
    .eq("user_id", userId)
    .maybeSingle();

  if (planError) throw planError;
  if (!planRow) throw new Error("Plan not found");

  const { error: deleteError } = await supabase
    .from("study_plans")
    .delete()
    .eq("id", planId)
    .eq("user_id", userId);

  if (deleteError) throw deleteError;
}

export async function activateStudyPlan({
  userId,
  planId,
}: {
  userId: string;
  planId: string;
}) {
  const { data: targetPlan, error: targetPlanError } = await supabase
    .from("study_plans")
    .select("id,status")
    .eq("id", planId)
    .eq("user_id", userId)
    .maybeSingle();

  if (targetPlanError) throw targetPlanError;
  if (!targetPlan) throw new Error("Plan not found");

  const now = new Date().toISOString();
  const { error: archiveError } = await supabase
    .from("study_plans")
    .update({ status: "archived", updated_at: now })
    .eq("user_id", userId)
    .eq("status", "active")
    .neq("id", planId);

  if (archiveError) throw archiveError;

  const { error: activateError } = await supabase
    .from("study_plans")
    .update({ status: "active", updated_at: now })
    .eq("id", planId)
    .eq("user_id", userId);

  if (activateError) throw activateError;
}
